import classNames from 'classnames'
import moment from 'moment-timezone'

import { useMyMonitoringDeleteUnitSchedule } from '@apps-common/apis/my-monitoring/schedules'
import { useDeleteUnitScheduled } from '@apps-common/apis/portal/schedules'
import { SchedulesOptionsModal } from '@apps-common/apps/portal/pages/units/pages/manage/pages/schedules/modals'
import { ButtonRoundLg } from '@apps-common/components/elements/button'
import { ModalButton } from '@apps-common/components/modals'
import { useHistoryModal } from '@apps-common/components/modals/unit-history/HistoryModal'
import { commonIcons } from '@apps-common/config/icons'
import { DAY_OPTIONS } from '@apps-common/config/schedules'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { invalidateScheduleQueries } from '@apps-common/lib/invalidateScheduleQueries'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IUnitScheduledModel } from '@apps-common/types/portal/unit-scheduled'
import { useScheduleModifyModal } from '../hooks/useScheduleModifyModal'

interface IProps {
  unitStatus: string
  unitId: string
  productId: string
  schedule: IUnitScheduledModel
  Modal?: any
  deleteHook?: any
  disabled?: boolean
}

export const ScheduleListCard: React.FC<IProps> = ({ unitStatus, unitId, productId, schedule, Modal, deleteHook, disabled }) => {
  const { unitScheduledId, active, scheduledName, controlName, daysOfWeek, timeToRun, desiredState, lastRun } = schedule
  const btnColor = useProjectHook('bg-brand text-on-brand', 'bg-ctx-mm-brand text-ctx-on-mm-brand')

  const { open } = useHistoryModal({ type: 'schedule', name: scheduledName, id: unitScheduledId })

  const useDeleteHook = useProjectHook(useDeleteUnitScheduled, useMyMonitoringDeleteUnitSchedule)

  const { mutate: deleteSchedule } = useDeleteHook(true, () => {
    invalidateScheduleQueries(unitScheduledId, unitId)
  })

  const { present } = useScheduleModifyModal({
    status: unitStatus,
    unitId,
    productId,
    scheduleId: unitScheduledId
  })

  const onOptionSelected = ({ action }: { action: string }) => {
    if (action === 'edit') {
      present()
    } else if (action === 'delete') {
      deleteSchedule({ unitId, scheduledId: unitScheduledId })
    } else if (action === 'history') {
      open()
    }
  }

  const getDays = () => {
    const dayNumbers = daysOfWeek?.split(',')

    return DAY_OPTIONS.filter((o) => dayNumbers.includes(o.value?.toString()))
      .map((o) => o.label)
      .join(', ')
  }

  const NavigateIcon = commonIcons.arrowAngled

  return (
    <ModalButton
      component={SchedulesOptionsModal}
      disabled={disabled}
      componentProps={{ schedule }}
      classes='relative w-full !bg-surface-2 text-text border border-border rounded-[35px] px-4 py-4 overflow-hidden !opacity-100'
      confirmCallback={onOptionSelected}
      backdropDismiss
      enterAnimation={modalSlideUpEnterAnimation}
      leaveAnimation={modalSlideUpLeaveAnimation}
    >
      <div className='flex flex-col items-between justify-start gap-[15px] text-left'>
        <div className='absolute top-0 right-0 flex items-center justify-end gap-[5px] px-4 py-2 bg-surface rounded-bl-[15px]'>
          <p className='jost-light text-[0.8em]'>{active ? 'Active' : 'Disabled'}</p>
          <div className='relative'>
            <div
              className={classNames(
                'absolute top-[1px] left-[1px] w-[13px] h-[13px] rounded-[10px]',
                active && 'bg-success animate-ping',
                !active && 'bg-error'
              )}
            />
            <div className={classNames('w-[15px] h-[15px] rounded-[10px]', active && 'bg-success', !active && 'bg-error')} />
          </div>
        </div>
        <p className='jost-light capitalize leading-[1em] pl-[15px] px-[7.5px] py-[3px]'>{scheduledName}</p>
        <div className='bg-surface flex items-end justify-between border border-border pl-5 pr-4 py-4 rounded-[35px]'>
          <div className='grid md:grid-cols-2 gap-[10px] grow'>
            <div>
              <label className='jost-medium text-[0.8em]'>Control</label>
              <p className='jost-light'>{controlName}</p>
            </div>
            <div>
              <label className='jost-medium text-[0.8em]'>Action</label>
              <p className='jost-light'>
                Turn {controlName} {desiredState}
              </p>
            </div>
            <div className='md:col-span-2'>
              <label className='jost-medium text-[0.8em]'>Running every</label>
              <p className='jost-light'>{getDays()}</p>
            </div>
            <div>
              <label className='jost-medium text-[0.8em]'>Running at</label>
              <p className='jost-light'>{moment(timeToRun, 'HH:mm:ss').format('HH:mm')}</p>
            </div>
            <div>
              <label className='jost-medium text-[0.8em]'>Last ran</label>
              <p className='jost-light'>{!!lastRun ? moment(lastRun).format('Do MMM YY, HH:mm:ss') : 'Never'}</p>
            </div>
          </div>
          {!disabled && (
            <ButtonRoundLg type='div' className={btnColor}>
              <NavigateIcon />
            </ButtonRoundLg>
          )}
        </div>
      </div>
    </ModalButton>
  )
}
